import React from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import Splash from "./splash";
import store, { persistor } from "./store";
import {
  ThemeProvider,
  CssBaseline,
  StyledEngineProvider,
  GlobalStyles
} from "@mui/material";
import theme from "./theme";

import * as Sentry from "@sentry/browser";
import packageInfo from "../package.json";
import { initializeGoogleAnalytics } from "./analytics";
import {
  captureSignupAttribution,
  initializeMetaPixel
} from "./metaConversions";
import {
  isRecaptchaTimeoutError,
  sentryEventHasRecaptchaTimeout
} from "./common/functions/recaptcha";

if (process.env.NODE_ENV === "production") {
  console.log("App version - " + packageInfo.version);

  Sentry.init({
    release: "signup@" + packageInfo.version,
    dsn: "https://5666a3f8540b4e7ab6ef38e2470a204e@sentry.io/1772420",
    beforeSend: (event, hint) => {
      if (
        isRecaptchaTimeoutError(hint && hint.originalException) ||
        sentryEventHasRecaptchaTimeout(event)
      ) {
        return null;
      }
      return event;
    }
  });
  initializeGoogleAnalytics();
  initializeMetaPixel();
}

captureSignupAttribution();

const App = props => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles
              styles={{
                html: { fontFamily: theme.typography.fontFamily },
                body: { fontFamily: theme.typography.fontFamily },
                "*": { fontFamily: theme.typography.fontFamily },
                "gmp-place-autocomplete": {
                  colorScheme: "light",
                  "--gmpx-color-surface": "#ffffff",
                  "--gmpx-color-on-surface": "#12384f",
                  "--gmpx-color-on-surface-variant": "#526a7a",
                  "--gmp-mat-color-surface": "#ffffff",
                  "--gmp-mat-color-on-surface": "#12384f",
                  "--gmp-mat-color-on-surface-variant": "#526a7a"
                },
                ".pac-container": {
                  backgroundColor: "#ffffff !important",
                  border: "1px solid rgba(9, 64, 102, 0.18) !important",
                  borderRadius: "0 0 8px 8px !important",
                  boxShadow: "0 12px 26px rgba(9, 64, 102, 0.18) !important",
                  color: "#12384f !important",
                  marginTop: "2px !important",
                  overflow: "hidden"
                },
                ".pac-container .pac-item": {
                  backgroundColor: "#ffffff !important",
                  borderTop: "1px solid rgba(9, 64, 102, 0.12) !important",
                  color: "#526a7a !important",
                  cursor: "pointer",
                  fontSize: "13px !important",
                  lineHeight: "1.35 !important",
                  padding: "10px 12px !important"
                },
                ".pac-container .pac-item:hover, .pac-container .pac-item-selected": {
                  backgroundColor: "#f2f7fb !important"
                },
                ".pac-container .pac-item-query": {
                  color: "#094066 !important",
                  fontSize: "14px !important",
                  fontWeight: "800 !important"
                },
                ".pac-container .pac-matched": {
                  color: "#094066 !important",
                  fontWeight: "900 !important"
                },
                ".pac-container .pac-secondary-text": {
                  color: "#526a7a !important"
                },
                ".pac-container .pac-icon": {
                  opacity: 0.7
                }
              }}
            />
            <BrowserRouter>
              <Splash />
            </BrowserRouter>
          </ThemeProvider>
        </StyledEngineProvider>
      </PersistGate>
    </Provider>
  );
};

ReactDOM.render(<App />, document.getElementById("root"));
